import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Descriptions,
    Badge,
    Switch,
    List
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    SyncOutlined,
    CloudServerOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';
import { useTranslation } from '../../../shared/i18n';

const { Option } = Select;
const { TextArea } = Input;

const ZoneManagement = () => {
    const { t } = useTranslation();
    const [zones, setZones] = useState([]);
    const [areaRows, setAreaRows] = useState(null); // null = region names dialog closed
    const [editingArea, setEditingArea] = useState(null);
    const [areaForm] = Form.useForm();
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [nodesModalVisible, setNodesModalVisible] = useState(false);
    const [currentZone, setCurrentZone] = useState(null);
    const [zoneNodes, setZoneNodes] = useState([]);
    const [nodesLoading, setNodesLoading] = useState(false);

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        loadZones();
        loadAreas();
    }, []);

    const loadZones = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getZoneList();
            if (response.status === 'success') {
                const zonesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.zones || []);
                setZones(zonesData);
            } else {
                message.error(response.msg || 'Failed to load zones');
            }
        } catch (error) {
            message.error('Failed to load zones');
        } finally {
            setLoading(false);
        }
    };

    const loadAreas = async () => {
        try {
            const response = await evmAdminService.getAreas();
            if (response.status === 'success') {
                const areasData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.areas || []);
                setAreas(areasData);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    };

    const loadZoneNodes = async (zoneId) => {
        setNodesLoading(true);
        try {
            const response = await evmAdminService.getZoneAvailableNodes(zoneId);
            if (response.status === 'success') {
                const nodesData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.nodes || []);
                setZoneNodes(nodesData);
            } else {
                message.error(response.msg || 'Failed to load zone nodes');
            }
        } catch (error) {
            message.error('Failed to load zone nodes');
        } finally {
            setNodesLoading(false);
        }
    };

    const showNodesModal = (zone) => {
        setCurrentZone(zone);
        loadZoneNodes(zone.id);
        setNodesModalVisible(true);
    };

    const handleDelete = async (zone) => {
        try {
            const response = await evmAdminService.deleteZone(zone.id);
            if (response.status === 'success') {
                message.success('Zone deleted');
                loadZones();
            } else {
                message.error(response.msg || 'Delete failed');
            }
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setCreateDrawerVisible(true);
    };

    const showEditDrawer = (zone) => {
        setCurrentZone(zone);
        editForm.setFieldsValue({
            ...zone,
            i18n: parseI18n(zone.i18n),
            vpc_enabled: zone.vpc_enabled ?? false
        });
        setEditDrawerVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmAdminService.createZone({ ...values, i18n: cleanI18n(values.i18n) });
            if (response.status === 'success') {
                message.success('Zone created');
                setCreateDrawerVisible(false);
                loadZones();
            } else {
                message.error(response.msg || 'Failed to create zone');
            }
        } catch (error) {
            message.error('Failed to create zone');
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmAdminService.updateZone(currentZone.id, { ...values, i18n: cleanI18n(values.i18n) });
            if (response.status === 'success') {
                message.success('Zone updated');
                setEditDrawerVisible(false);
                loadZones();
            } else {
                message.error(response.msg || 'Failed to update zone');
            }
        } catch (error) {
            message.error('Failed to update zone');
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'active': { color: 'green', text: 'Active' },
            'inactive': { color: 'red', text: 'Inactive' },
            'maintenance': { color: 'orange', text: 'Maintenance' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getAreaName = (areaId) => {
        const area = areas.find(a => a.id === areaId);
        return area ? <><CountryFlag code={area.country} />{area.name}</> : `Area ${areaId}`;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 120
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 180
        },
        {
            title: 'Area',
            dataIndex: 'area_id',
            key: 'area_id',
            width: 150,
            render: (areaId) => getAreaName(areaId)
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'VPC Enabled',
            dataIndex: 'vpc_enabled',
            key: 'vpc_enabled',
            width: 120,
            render: (enabled) => enabled ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
            ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>No</Tag>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Nodes">
                        <Button
                            size="small"
                            icon={<CloudServerOutlined />}
                            onClick={() => showNodesModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditDrawer(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this zone?"
                        description="This will not affect existing nodes."
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const openAreas = async () => {
        const r = await evmAdminService.getAreaNames();
        if (r.status === 'success') setAreaRows(r.data?.areas || []);
        else message.error(r.msg || t('Failed to load'));
    };
    const editArea = (a) => {
        setEditingArea(a);
        areaForm.setFieldsValue({ name: a.name, i18n: parseI18n(a.i18n) });
    };
    const saveArea = async () => {
        const v = await areaForm.validateFields();
        const r = await evmAdminService.updateAreaName(editingArea.id, { name: v.name, i18n: cleanI18n(v.i18n) });
        if (r.status !== 'success') { message.error(r.msg || t('Failed to save')); return; }
        message.success(t('Saved'));
        setEditingArea(null);
        openAreas();
        loadAreas();
    };

    const ZoneForm = ({ form, onFinish, isEdit = false }) => (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
                name="code"
                label="Zone Code"
                rules={[{ required: true, message: 'Please enter zone code' }]}
                tooltip="Unique identifier, e.g., us-east-1a"
            >
                <Input placeholder="e.g., us-east-1a" disabled={isEdit} />
            </Form.Item>

            <I18nFieldTabs
                fields={[{ key: 'name', label: 'Zone Name' }, { key: 'description', label: 'Description', type: 'textarea' }]}
                base={<>
                    <Form.Item
                        name="name"
                        label="Zone Name"
                        rules={[{ required: true, message: 'Please enter zone name' }]}
                    >
                        <Input placeholder="e.g., US East (N. Virginia) - AZ1" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <TextArea rows={3} placeholder="Zone description" />
                    </Form.Item>
                </>}
            />

            <Form.Item
                name="area_id"
                label="Area"
                rules={[{ required: true, message: 'Please select area' }]}
            >
                <Select placeholder="Select area" disabled={isEdit}>
                    {areas.map(area => (
                        <Option key={area.id} value={area.id}><CountryFlag code={area.country} />{area.name} ({area.code})</Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                    <Option value="maintenance">Maintenance</Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="vpc_enabled"
                label="VPC Enabled"
                valuePropName="checked"
                tooltip="Enable VPC/VXLAN networking in this zone"
            >
                <Switch />
            </Form.Item>

            <Form.Item name="sort" label="Sort Order" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
        </Form>
    );

    return (
        <div>
            <Card
                title={<span><GlobalOutlined /> 可用区管理</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>
                            Add Zone
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadZones}>
                            Refresh
                        </Button>
                        <Button icon={<GlobalOutlined />} onClick={openAreas}>{t('Region names')}</Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={zones}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1000 }}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            {/* Create Zone Drawer */}
            <Drawer
                title="添加可用区"
                width={450}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>Create</Button>
                    </Space>
                }
            >
                <ZoneForm form={createForm} onFinish={handleCreate} />
            </Drawer>

            {/* Edit Zone Drawer */}
            <Drawer
                title="编辑可用区"
                width={450}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setEditDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => editForm.submit()}>Save</Button>
                    </Space>
                }
            >
                <ZoneForm form={editForm} onFinish={handleUpdate} isEdit />
            </Drawer>

            {/* Region names (customer-facing, per language) */}
            <Modal title={t('Region names')} open={areaRows !== null} onCancel={() => setAreaRows(null)} footer={null} width={560}>
                <Table size="small" rowKey="id" pagination={false} dataSource={areaRows || []} columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: t('Name'), dataIndex: 'name', render: (v, a) => (Object.keys(parseI18n(a.i18n)).length ? <Badge dot offset={[6, 0]}>{v}</Badge> : v) },
                    { title: t('Code'), dataIndex: 'remark', width: 80, render: (v) => <><CountryFlag code={v} />{v}</> },
                    { title: '', key: 'op', width: 70, render: (_, a) => <Button type="link" size="small" icon={<EditOutlined />} onClick={() => editArea(a)} /> },
                ]} />
            </Modal>
            <Modal title={editingArea?.name} open={!!editingArea} onCancel={() => setEditingArea(null)} onOk={saveArea} destroyOnClose>
                <Form form={areaForm} layout="vertical" preserve={false}>
                    <I18nFieldTabs fields={[{ key: 'name', label: t('Name') }]}
                        base={<Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true }]}><Input maxLength={64} /></Form.Item>} />
                </Form>
            </Modal>

            {/* Zone Nodes Modal */}
            <Modal
                title={`Nodes in Zone: ${currentZone?.name || ''}`}
                open={nodesModalVisible}
                onCancel={() => setNodesModalVisible(false)}
                footer={null}
                width={600}
            >
                <List
                    loading={nodesLoading}
                    dataSource={zoneNodes}
                    locale={{ emptyText: 'No nodes in this zone' }}
                    renderItem={node => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<CloudServerOutlined style={{ fontSize: 24 }} />}
                                title={node.name}
                                description={
                                    <Space>
                                        <span>IP: {node.ipaddress}</span>
                                        <Tag color={node.status === 'enable' ? 'green' : 'red'}>
                                            {node.status}
                                        </Tag>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default ZoneManagement;
